import { useEffect, useRef, useState } from 'react';
import type { StreamSummary, TransportHistoryPoint, TransportMessage, TsHealth } from '@shared/protocol';
import { api } from '../lib/api';
import type { TransportPoint } from '../lib/useStreamLive';
import { Panel, Segmented } from './Panel';
import { LevelBadge } from './Status';
import { TimeGraph } from './TimeGraph';

const f = (v: number | null | undefined, d = 1, unit = '') => (v === null || v === undefined ? '--' : `${v.toFixed(d)}${unit}`);

const TR = [
  { key: 'syncErrors', label: '1.1/1.2 TS sync', p: 1 },
  { key: 'patErrors', label: '1.3 PAT', p: 1 },
  { key: 'continuityErrors', label: '1.4 Continuity', p: 1 },
  { key: 'pmtErrors', label: '1.5 PMT', p: 1 },
  { key: 'pidErrors', label: '1.6 PID', p: 1 },
  { key: 'transportErrors', label: '2.1 Transport error', p: 2 },
  { key: 'pcrRepetitionErrors', label: '2.3 PCR repetition', p: 2 },
  { key: 'pcrDiscontinuities', label: '2.3 PCR discontinuity', p: 2 },
  { key: 'ptsErrors', label: '2.5 PTS', p: 2 },
] as const;

function TsChecks({ ts }: { ts: TsHealth }) {
  return (
    <div className="ts-checks">
      <h4>MPEG-TS (TSDuck)</h4>
      <dl className="kv">
        <dt>TS bitrate</dt>
        <dd className="num">{ts.bitrate ? `${(ts.bitrate / 1e6).toFixed(2)} Mb/s` : '--'}</dd>
        <dt>Services</dt>
        <dd>{ts.services.map((s) => `${s.name || 'unnamed'} (id ${s.id}${s.provider ? `, ${s.provider}` : ''})`).join('; ') || '--'}</dd>
        <dt>SCTE-35</dt>
        <dd>{ts.scte35.present ? `present, ${ts.scte35.events} splice event(s)${ts.scte35.lastAt ? `, last ${new Date(ts.scte35.lastAt).toLocaleTimeString('en-GB')}` : ''}` : 'not present'}</dd>
        <dt>Captions</dt>
        <dd>
          {[ts.captions.cea608 && 'CEA-608/708 in video', ts.captions.dvbSubtitles && 'DVB subtitles', ts.captions.teletext && 'Teletext'].filter(Boolean).join(', ') || 'none detected'}
        </dd>
      </dl>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>PID</th>
            <th>Type</th>
            <th>Description</th>
            <th className="num">Bitrate</th>
          </tr>
        </thead>
        <tbody>
          {ts.pids.map((p) => (
            <tr key={p.pid}>
              <td className="mono">
                {p.pid} <span className="muted">(0x{p.pid.toString(16)})</span>
              </td>
              <td>{p.kind}</td>
              <td>{p.description}</td>
              <td className="num">{p.bitrate >= 1e6 ? `${(p.bitrate / 1e6).toFixed(2)} Mb/s` : `${Math.round(p.bitrate / 1000)} kb/s`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4>ETSI TR 101 290 indicators</h4>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>Check</th>
            <th>Now</th>
            <th className="num">Session total</th>
          </tr>
        </thead>
        <tbody>
          {TR.map((r) => {
            const now = ts.recent[r.key];
            return (
              <tr key={r.key}>
                <td>
                  {r.label} <span className="muted small">P{r.p}</span>
                </td>
                <td>{now > 0 ? <LevelBadge level={r.p === 1 ? 'fail' : 'warn'}>{now} in last interval</LevelBadge> : <LevelBadge level="pass">OK</LevelBadge>}</td>
                <td className="num">{ts.counters[r.key]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** HLS delivery health or SRT statistics, with a time graph, and MPEG-TS checks for SRT. */
export function TransportPanel({
  stream,
  transport,
  series,
}: {
  stream: StreamSummary | null;
  transport: TransportMessage | null;
  series: React.RefObject<TransportPoint[]>;
}) {
  const [minutes, setMinutes] = useState(5);
  const history = useRef<TransportPoint[]>([]);
  const id = stream?.id;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api<{ points: TransportHistoryPoint[] }>('GET', `/api/streams/${id}/transport-history?minutes=${minutes}`)
      .then((r) => {
        if (cancelled) return;
        history.current = r.points.map((p) => ({ t: p.t, rtt: p.rttMs, loss: p.lossPercent, rx: p.receiveMbps, buffer: p.bufferMs, latency: p.hlsLatency, age: p.sinceNewSegment, bitrate: p.videoBitrate }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, minutes]);

  const points = () => {
    const live = series.current ?? [];
    const lastHist = history.current.at(-1)?.t ?? 0;
    const all = [...history.current, ...live.filter((p) => p.t > lastHist)];
    return all;
  };

  const srt = transport?.srt ?? null;
  const hls = transport?.hls ?? null;
  const ts = transport?.ts ?? null;
  const isSrt = stream?.type === 'srt';

  return (
    <Panel
      title={isSrt ? 'Transport: SRT' : 'Transport: HLS delivery'}
      actions={<Segmented<number> label="Graph range" value={minutes} onChange={setMinutes} options={[1, 5, 15, 60].map((m) => ({ value: m, label: `${m} min` }))} />}
    >
      {isSrt ? (
        <>
          <dl className="kv kv-columns">
            <dt>Connection</dt>
            <dd>{srt?.connected ? <LevelBadge level="pass">Connected</LevelBadge> : <LevelBadge level="warn">Not connected</LevelBadge>}</dd>
            <dt>Round-trip time</dt>
            <dd className="num">{f(srt?.rttMs, 1, ' ms')}</dd>
            <dt>Receive rate</dt>
            <dd className="num">{f(srt?.receiveMbps, 2, ' Mb/s')}</dd>
            <dt>Link bandwidth (estimate)</dt>
            <dd className="num">{f(srt?.bandwidthMbps, 0, ' Mb/s')}</dd>
            <dt>Loss, last 10 s</dt>
            <dd className="num">{f(srt?.lossPercent, 2, ' %')}</dd>
            <dt>Lost / retransmitted / dropped</dt>
            <dd className="num">{srt ? `${srt.totalLost} / ${srt.totalRetransmitted} / ${srt.totalDropped}` : '--'}</dd>
            <dt>Receive buffer</dt>
            <dd className="num">{f(srt?.bufferMs, 0, ' ms')}</dd>
            <dt>Negotiated latency</dt>
            <dd className="num">{f(srt?.latencyMs, 0, ' ms')}</dd>
            <dt>Flight size</dt>
            <dd className="num">{srt?.flightSize ?? '--'}</dd>
            <dt>Disconnects</dt>
            <dd className="num">{srt?.disconnects ?? 0}</dd>
          </dl>
          <TimeGraph
            label="SRT round-trip time and packet loss over time"
            minutes={minutes}
            left={{ min: 0, max: Math.max(50, ...points().map((p) => p.rtt ?? 0)) * 1.1, unit: '' }}
            right={{ min: 0, max: Math.max(1, ...points().map((p) => p.loss ?? 0)), unit: '' }}
            data={() => {
              const p = points();
              return {
                t: p.map((x) => x.t),
                series: [
                  { label: 'Loss %', color: 'var(--meter-over)', values: p.map((x) => x.loss), bars: true, axis: 'right' },
                  { label: 'RTT ms', color: getComputedStyle(document.documentElement).getPropertyValue('--graph-short').trim(), values: p.map((x) => x.rtt) },
                ],
              };
            }}
          />
          <div className="legend">
            <span>
              <i className="swatch" style={{ background: 'var(--graph-short)' }} /> RTT (ms, left)
            </span>
            <span>
              <i className="swatch" style={{ background: 'var(--meter-over)' }} /> Packet loss (%, right)
            </span>
          </div>
          {ts ? <TsChecks ts={ts} /> : <p className="muted small">MPEG-TS checks start when the stream is received.</p>}
        </>
      ) : (
        <>
          {!hls ? (
            <p className="muted">{stream?.type === 'youtube' && transport?.source?.live === false ? 'Recordings are read as a file; there is no playlist to monitor.' : 'Playlist health appears once the stream is being monitored.'}</p>
          ) : (
            <dl className="kv kv-columns">
              <dt>Playlist</dt>
              <dd>{hls.endList ? <LevelBadge level="warn">Ended (ENDLIST)</LevelBadge> : (hls.sinceNewSegment ?? 0) > Math.max(3 * (hls.targetDuration ?? 6), 15) ? <LevelBadge level="fail">Not updating</LevelBadge> : <LevelBadge level="pass">Updating</LevelBadge>}</dd>
              <dt>Target duration</dt>
              <dd className="num">{f(hls.targetDuration, 0, ' s')}</dd>
              <dt>Last segment</dt>
              <dd className="num">{f(hls.lastSegmentDuration, 2, ' s')}</dd>
              <dt>Since new segment</dt>
              <dd className="num">{f(hls.sinceNewSegment, 1, ' s')}</dd>
              <dt>Refresh interval</dt>
              <dd className="num">{f(hls.refreshInterval, 1, ' s')}</dd>
              <dt>Latency behind live</dt>
              <dd className="num">{hls.latency === null ? 'needs PROGRAM-DATE-TIME' : f(hls.latency, 1, ' s')}</dd>
              <dt>Missing / late segments</dt>
              <dd className="num">
                {hls.missingSegments} / {hls.lateSegments}
              </dd>
              <dt>Segments over target</dt>
              <dd className="num">{hls.overlongSegments}</dd>
              <dt>Playlist errors</dt>
              <dd>
                {hls.playlistErrors}
                {hls.lastError ? <span className="muted"> (last: {hls.lastError})</span> : null}
              </dd>
            </dl>
          )}
          <TimeGraph
            label="HLS latency and time since the last new segment"
            minutes={minutes}
            left={{ min: 0, max: Math.max(10, ...points().map((p) => Math.max(p.latency ?? 0, p.age ?? 0))) * 1.1, unit: ' s' }}
            data={() => {
              const p = points();
              const css = getComputedStyle(document.documentElement);
              return {
                t: p.map((x) => x.t),
                series: [
                  { label: 'Since new segment', color: css.getPropertyValue('--graph-momentary').trim(), values: p.map((x) => x.age) },
                  { label: 'Latency', color: css.getPropertyValue('--graph-short').trim(), values: p.map((x) => x.latency) },
                ],
              };
            }}
          />
          <div className="legend">
            <span>
              <i className="swatch" style={{ background: 'var(--graph-short)' }} /> Latency behind live
            </span>
            <span>
              <i className="swatch" style={{ background: 'var(--graph-momentary)' }} /> Time since a new segment appeared
            </span>
          </div>
        </>
      )}
    </Panel>
  );
}
